import { format } from 'date-fns'
import { Link } from 'react-router-dom'
import {
  Droplets,
  Fish,
  Wrench,
  AlertTriangle,
  Activity,
  Thermometer,
  ExternalLink,
} from 'lucide-react'
import type { TimelineEvent, Severity } from '../types'
import { SEVERITY_COLORS } from '../types'
import { buildRoute } from '@/shared/lib/constants'

interface TimelineProps {
  events: TimelineEvent[]
  farmId: string
  pondId?: string
  maxItems?: number
}

const EVENT_ICONS: Record<string, React.ElementType> = {
  water_quality: Droplets,
  fish_health: Fish,
  maintenance: Wrench,
  feed: Activity,
  alert: AlertTriangle,
  environmental: Thermometer,
}

// Helper to build the detail page link based on record type
function getRecordLink(
  farmId: string,
  pondId: string | undefined,
  recordId: string,
  recordType: string
): string | null {
  switch (recordType) {
    case 'water_quality':
      // Water quality requires pondId
      if (pondId) {
        return buildRoute.waterQualityTest(farmId, pondId, recordId)
      }
      return null
    case 'fish_health':
      return buildRoute.fishHealthDetail(farmId, recordId, pondId)
    case 'feed':
      return buildRoute.feedDetail(farmId, recordId, pondId)
    case 'maintenance':
      return buildRoute.maintenanceDetail(farmId, recordId, pondId)
    default:
      return null
  }
}

export function Timeline({ events, farmId, pondId, maxItems = 20 }: TimelineProps) {
  const displayEvents = events.slice(0, maxItems)

  if (displayEvents.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200/50 bg-slate-50 p-6 text-center">
        <p className="text-sm text-slate-500">No events in timeline</p>
      </div>
    )
  }

  return (
    <div className="relative">
      {/* Timeline line */}
      <div className="absolute left-4 top-0 h-full w-0.5 bg-slate-200" />

      <div className="space-y-4">
        {displayEvents.map((event, index) => {
          const Icon = EVENT_ICONS[event.eventType] || Activity
          const severityClass = SEVERITY_COLORS[event.severity as Severity] || SEVERITY_COLORS.low

          // Get link to source record if available
          const recordLink = event.recordId && event.recordType
            ? getRecordLink(farmId, pondId, event.recordId, event.recordType)
            : null

          return (
            <div key={index} className="relative flex gap-4 pl-10">
              {/* Icon node */}
              <div
                className={`absolute left-0 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-white shadow-sm ${
                  event.severity === 'critical' || event.severity === 'high'
                    ? 'ring-2 ring-red-200'
                    : ''
                }`}
              >
                <Icon className="h-4 w-4 text-slate-600" />
              </div>

              {/* Event card */}
              <div className="flex-1 rounded-lg border border-slate-200/50 bg-white p-3 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-slate-900">
                      {event.title}
                    </h4>
                    <p className="mt-1 text-sm text-slate-600">
                      {event.description}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {recordLink && (
                      <Link
                        to={recordLink}
                        className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-emerald-600 hover:bg-emerald-50 transition-colors"
                        title="View source record"
                      >
                        <ExternalLink className="h-3 w-3" />
                        View
                      </Link>
                    )}
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${severityClass}`}
                    >
                      {event.severity}
                    </span>
                  </div>
                </div>

                {/* Metrics if available */}
                {event.metrics && Object.keys(event.metrics).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {Object.entries(event.metrics).map(([key, value]) => (
                      <span
                        key={key}
                        className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600"
                      >
                        {key}: {String(value)}
                      </span>
                    ))}
                  </div>
                )}

                {/* Timestamp */}
                <p className="mt-2 text-xs text-slate-400">
                  {format(new Date(event.timestamp), 'MMM d, yyyy h:mm a')}
                </p>
              </div>
            </div>
          )
        })}
      </div>

      {events.length > maxItems && (
        <div className="mt-4 text-center">
          <p className="text-sm text-slate-500">
            Showing {maxItems} of {events.length} events
          </p>
        </div>
      )}
    </div>
  )
}

export default Timeline
